<template>
  <!-- Import systems from Excel or CSV: upload, match columns, review, done (US 9370) -->
  <div class="pm-page so-page">
    <div class="cl-head im-head">
      <div>
        <router-link to="/systems" class="v2-link cl-back"><font-awesome-icon icon="angle-left"/>{{ $t('systems.title') }}</router-link>
        <h1>{{ $t('system_import.title') }}</h1>
        <p v-if="file">{{ file }}<template v-if="sheet"> · {{ $tc('system_import.rows', sheet.total, { count: sheet.total }) }} · {{ sheet.name }}</template></p>
        <p v-else>{{ $t('system_import.intro') }}</p>
      </div>
      <div class="im-steps">
        <template v-for="(item, index) in steps">
          <span v-if="index" :key="item + '-line'" class="im-steps__line"></span>
          <span :key="item" class="im-steps__step" :class="{ 'is-done': index < stepIndex, 'is-cur': index === stepIndex }">
            <i>
              <font-awesome-icon v-if="index < stepIndex" icon="check"/>
              <template v-else>{{ index + 1 }}</template>
            </i>{{ $t('system_import.steps.' + item) }}
          </span>
        </template>
      </div>
    </div>

    <!-- 1. Upload -->
    <section v-if="step === 'upload'" class="v2-card im-upload" :class="{ 'is-over': dragging }"
        @dragover.prevent="dragging = true" @dragleave.prevent="dragging = false" @drop.prevent="drop">
      <font-awesome-icon icon="cloud-upload-alt" class="im-upload__icon"/>
      <h3>{{ $t('system_import.upload.title') }}</h3>
      <p>{{ $t('system_import.upload.text') }}</p>
      <label class="btn btn-success" qid="import-choose-file">
        {{ $t('system_import.upload.choose') }}
        <input type="file" accept=".xlsx,.xls,.csv" class="d-none" @change="choose"/>
      </label>
      <b-alert v-if="error" show variant="danger" class="mt-3">{{ error }}</b-alert>
      <small class="im-upload__hint">{{ $t('system_import.upload.hint') }}</small>
    </section>

    <!-- 2. Match columns -->
    <section v-if="step === 'match'" class="v2-card im-card">
      <div class="pm-map__head">
        <div class="pm-map__titles">
          <h3>{{ $t('system_import.match.title') }}</h3>
          <span class="v2-card__sub">{{ $t('system_import.match.text') }}</span>
        </div>
        <b-form-select v-if="sheets.length > 1" v-model="sheetIndex" class="im-sheet" qid="import-sheet" @change="pickSheet">
          <option v-for="(item, index) in sheets" :key="index" :value="index">{{ item.name }}</option>
        </b-form-select>
      </div>
      <div class="im-map">
        <div v-for="field in fields" :key="field" class="sp-kv">
          <div class="sp-kv__k">{{ $t('system_import.fields.' + field) }}<span v-if="field === 'name'" class="sp-req"></span></div>
          <div class="sp-kv__v">
            <b-form-select v-model="mapping[field]" class="im-select" :qid="'import-map-' + field">
              <option :value="null">{{ $t('system_import.match.none') }}</option>
              <option v-for="(header, index) in sheet.headers" :key="index" :value="index">{{ header || $t('system_import.match.column', { n: index + 1 }) }}</option>
            </b-form-select>
            <small class="sp-sub">{{ sample(field) }}</small>
          </div>
        </div>
      </div>
      <div class="pm-foot so-foot">
        <span>{{ $t('system_import.match.help') }}</span>
        <span class="so-foot__count"></span>
        <button type="button" class="btn btn-secondary" @click="reset">{{ $t('system_import.back') }}</button>
        <button type="button" class="btn btn-success" :disabled="mapping.name === null || busy" qid="import-preview" @click="preview">{{ $t('system_import.match.next') }}</button>
      </div>
    </section>

    <!-- 3. Review -->
    <template v-if="step === 'review'">
      <div class="cl-tiles im-tiles">
        <div class="cl-tile"><b>{{ plan.counts.rows }}</b><span>{{ $t('system_import.review.rows') }}</span><small>{{ $t('system_import.review.rows_sub') }}</small></div>
        <div class="cl-tile is-teal"><b>{{ plan.counts.new }}</b><span>{{ $t('system_import.review.new') }}</span><small>{{ $tc('system_import.review.new_sub', plan.counts.contracts, { count: plan.counts.contracts }) }}</small></div>
        <div class="cl-tile is-blue"><b>{{ plan.counts.update }}</b><span>{{ $t('system_import.review.update') }}</span><small>{{ $t('system_import.review.update_sub') }}</small></div>
        <div class="cl-tile"><b>{{ plan.counts.merge }}</b><span>{{ $t('system_import.review.merge') }}</span><small>{{ $t('system_import.review.merge_sub') }}</small></div>
        <div class="cl-tile is-amber"><b>{{ plan.counts.attention }}</b><span>{{ $t('system_import.review.attention') }}</span><small>{{ $t('system_import.review.attention_sub') }}</small></div>
      </div>

      <div class="im-review">
        <section class="v2-card so-card">
          <div class="pm-map__head">
            <div class="pm-map__titles">
              <h3>{{ $t('system_import.review.title') }}</h3>
              <span class="v2-card__sub">{{ $t('system_import.review.text') }}</span>
            </div>
          </div>
          <div class="so-table">
            <div class="so-row so-row--head im-grid">
              <span></span>
              <span>{{ $t('system_import.review.in_file') }}</span>
              <span>{{ $t('system_import.review.becomes') }}</span>
              <span>{{ $t('system_import.fields.runs_as') }}</span>
              <span>{{ $t('system_import.fields.owner') }}</span>
              <span>{{ $t('system_import.review.action') }}</span>
            </div>
            <div v-for="row in plan.rows" :key="row.row" class="so-row im-grid" :class="{ 'is-dim': !isIncluded(row) }">
              <span>
                <input v-if="['new', 'update'].includes(row.action)" type="checkbox" class="so-check" :checked="isIncluded(row)" :aria-label="row.name" @change="toggle(row, $event.target.checked)"/>
              </span>
              <span class="im-raw">{{ row.name || '–' }}</span>
              <span class="so-two">
                <template v-if="row.action === 'merge'">{{ row.name }}<small>{{ $t('system_import.review.same_as', { row: row.merged_into }) }}</small></template>
                <template v-else-if="row.action === 'attention'">–<small>{{ $t('system_import.review.no_name') }}</small></template>
                <template v-else>
                  <router-link v-if="row.existing" :to="{ name: 'systems.view', params: { slug: row.existing.slug } }">{{ row.existing.name }}</router-link>
                  <b v-else>{{ row.name }}</b>
                  <small>{{ becomesNote(row) }}</small>
                </template>
              </span>
              <span class="so-two">
                {{ row.runs_as ? $t('systems_overview.runs_as.' + row.runs_as) : '–' }}
                <small v-if="row.runs_as_text && !row.runs_as">{{ $t('system_import.review.not_understood', { text: row.runs_as_text }) }}</small>
              </span>
              <span class="so-two">
                <template v-if="row.owner">{{ row.owner.name }}</template>
                <span v-else-if="row.owner_text" class="so-chip so-chip--miss">{{ row.owner_text }}</span>
                <span v-else class="so-muted">–</span>
                <small v-if="row.owner_text">{{ $t('system_import.review.owner_unknown') }}</small>
              </span>
              <span><span class="im-badge" :class="'is-' + row.action">{{ actionLabel(row) }}</span></span>
            </div>
          </div>
          <div class="pm-foot so-foot">
            <span>{{ $t('systems_overview.showing', { shown: plan.rows.length, total: plan.counts.rows }) }}</span>
            <span class="so-foot__count"></span>
            <button type="button" class="btn btn-secondary" @click="step = 'match'">{{ $t('system_import.back') }}</button>
            <button type="button" class="btn btn-success" :disabled="!toImport || busy" qid="import-save" @click="save">
              <font-awesome-icon icon="cloud-upload-alt"/> {{ $tc('system_import.review.import', toImport, { count: toImport }) }}
            </button>
          </div>
        </section>

        <section class="v2-card sp-side__card">
          <h4><font-awesome-icon icon="info-circle"/>{{ $t('system_import.what.title') }}</h4>
          <div class="sp-row"><span>{{ $t('system_import.what.existing') }}</span></div>
          <div class="sp-row"><span>{{ $t('system_import.what.contracts') }}</span></div>
          <div class="sp-row"><span>{{ $tc('system_import.what.owners', plan.counts.owners_unknown, { count: plan.counts.owners_unknown }) }}</span></div>
          <div class="sp-row"><span>{{ $t('system_import.what.log') }}</span></div>
        </section>
      </div>
    </template>

    <!-- 4. Done -->
    <section v-if="step === 'done'" class="v2-card im-upload">
      <font-awesome-icon icon="check-circle" class="im-upload__icon is-ok"/>
      <h3>{{ $t('system_import.done.title') }}</h3>
      <p>{{ $t('system_import.done.text', { created: result.created, updated: result.updated }) }}</p>
      <b-alert v-if="result.failed.length" show variant="warning">{{ $t('system_import.done.failed', { rows: result.failed.join(', ') }) }}</b-alert>
      <div>
        <router-link to="/systems" class="btn btn-success">{{ $t('system_import.done.open') }}</router-link>
        <button type="button" class="btn btn-secondary ml-2" @click="reset">{{ $t('system_import.done.another') }}</button>
      </div>
    </section>
  </div>
</template>

<script>
  import piincHttp from '@/util/configureAxios.js';

  const FIELDS = ['name', 'description', 'owner', 'runs_as', 'location', 'contract', 'tags'];

  export default {
    name: 'ImportSystems',
    data: function() {
      return {
        step: 'upload',
        steps: ['upload', 'match', 'review', 'done'],
        fields: FIELDS,
        file: '',
        sheets: [],
        sheetIndex: 0,
        mapping: {},
        plan: null,
        excluded: [],
        result: null,
        dragging: false,
        busy: false,
        error: ''
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      stepIndex() {
        return this.steps.indexOf(this.step);
      },
      sheet() {
        return this.sheets[this.sheetIndex];
      },
      toImport() {
        return this.plan ? this.plan.rows.filter(row => ['new', 'update'].includes(row.action) && this.isIncluded(row)).length : 0;
      }
    },
    methods: {
      choose(event) {
        let file = event.target.files[0];
        event.target.value = '';
        if (file) {
          this.upload(file);
        }
      },
      drop(event) {
        this.dragging = false;
        let file = event.dataTransfer.files[0];
        if (file) {
          this.upload(file);
        }
      },
      upload(file) {
        let self = this;
        let formData = new FormData();
        formData.append('client', this.getClient.slug);
        formData.append('file', file);
        this.error = '';
        this.busy = true;
        piincHttp.post('systems/import/read', formData, { headers: { 'Content-Type': 'multipart/form-data' } }).then(function(response) {
          self.busy = false;
          if (!response.data.sheets.length) {
            self.error = self.$t('system_import.upload.empty');
            return;
          }
          self.file = response.data.file;
          self.sheets = response.data.sheets;
          self.pickSheet(0);
          self.step = 'match';
        }, function(error) {
          self.busy = false;
          self.error = (error && error.data && error.data.message) || self.$t('system_import.upload.failed');
        });
      },
      pickSheet(index) {
        this.sheetIndex = index;
        this.mapping = Object.assign({}, this.sheets[index].mapping);
      },
      // The first values of the chosen column, so the user can see it's the right one
      sample(field) {
        let column = this.mapping[field];
        if (column === null || column === undefined) {
          return '';
        }
        let values = this.sheet.rows.map(row => row[column]).filter(Boolean).slice(0, 3);
        return values.length ? this.$t('system_import.match.sample', { values: values.join(', ') }) : this.$t('system_import.match.empty_column');
      },
      rows() {
        let mapping = this.mapping;
        return this.sheet.rows.map(row => {
          let item = {};
          FIELDS.forEach(field => {
            item[field] = mapping[field] === null || mapping[field] === undefined ? '' : (row[mapping[field]] || '');
          });
          return item;
        });
      },
      preview() {
        let self = this;
        this.busy = true;
        piincHttp.post('systems/import/preview', { client: this.getClient.slug, rows: this.rows() }).then(function(response) {
          self.busy = false;
          self.plan = response.data;
          self.excluded = [];
          self.step = 'review';
        }, function() {
          self.busy = false;
        });
      },
      isIncluded(row) {
        return !this.excluded.includes(row.row);
      },
      toggle(row, checked) {
        this.excluded = checked ? this.excluded.filter(item => item !== row.row) : this.excluded.concat([row.row]);
      },
      becomesNote(row) {
        if (row.action === 'update') {
          return row.fills.length
            ? this.$t('system_import.review.fills', { fields: row.fills.map(field => this.$t('system_import.fields.' + field).toLowerCase()).join(', ') })
            : this.$t('system_import.review.nothing_to_fill');
        }
        let parts = [];
        if (row.contract && !row.contract.linked) {
          parts.push(this.$t('system_import.review.links', { name: row.contract.name }));
        } else if (row.contract_text) {
          parts.push(this.$t('system_import.review.no_contract', { name: row.contract_text }));
        }
        return parts.join(' · ');
      },
      actionLabel(row) {
        if (row.action === 'update' && !row.fills.length && !(row.contract && !row.contract.linked)) {
          return this.$t('system_import.review.actions.unchanged');
        }
        return this.$t('system_import.review.actions.' + row.action);
      },
      save() {
        let self = this;
        this.busy = true;
        let rows = this.rows().map((row, index) => Object.assign(row, { include: !this.excluded.includes(index + 1) }));
        piincHttp.post('systems/import/save', { client: this.getClient.slug, rows: rows }).then(function(response) {
          self.busy = false;
          self.result = response.data;
          self.step = 'done';
        }, function() {
          self.busy = false;
        });
      },
      reset() {
        this.step = 'upload';
        this.file = '';
        this.sheets = [];
        this.plan = null;
        this.result = null;
        this.error = '';
      }
    }
  }
</script>
